import equal from "fast-deep-equal";
import PropTypes, { arrayOf, object } from "prop-types";
import React, { Fragment, memo } from "react";
import { twMerge } from "tailwind-merge";

import Divider from "components/shared/Divider";
import CloseIcon from "components/shared/Icons/Close";
import PriceFormatter from "components/shared/PriceFormatter";
import Typography from "components/shared/Typography";

const itemShape = PropTypes.shape({
  type: PropTypes.oneOf([
    "item",
    "items",
    "promocode",
    "divider",
    "formattedPrice",
    "group",
    "typography",
    "content",
    "customItem",
  ]).isRequired,
  isVisible: PropTypes.any,
  primaryText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  secondaryText: PropTypes.string,
  dataId: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  content: PropTypes.node,
  className: PropTypes.string,
  isRemovable: PropTypes.bool,
  onRemove: PropTypes.func,
  removeTitle: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      primaryText: PropTypes.string,
      secondaryText: PropTypes.string,
      price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      dataId: PropTypes.string,
      type: PropTypes.string,
      content: PropTypes.node,
    })
  ),
});

const BasicItem = ({ item }) => {
  if (!item.isVisible) {
    return null;
  }

  switch (item.type) {
    case "content":
      return item.content;

    case "customItem":
      return (
        <li className="my-1">
          <div className="flex justify-between items-center">
            <Typography className="uppercase font-semibold">
              {item.primaryText}
            </Typography>
            {item.content}
          </div>
        </li>
      );

    case "divider":
      return <Divider className={twMerge("bg-greyLight", item.className)} />;

    case "formattedPrice":
      return (
        <li className="my-1">
          <div className="flex justify-between items-center italic">
            <Typography className="uppercase">{item.primaryText}</Typography>
            <Typography className="uppercase">{item.price}</Typography>
          </div>
        </li>
      );

    case "item":
      return (
        <li className={twMerge("my-1", item.className)}>
          <div className="flex justify-between items-center">
            <Typography className="uppercase font-semibold">
              {item.primaryText}
            </Typography>
            <PriceFormatter
              amount={item.price}
              data-id={item.dataId}
              showOnlyCash
            />
          </div>
        </li>
      );

    case "items":
      return (
        <li className="my-1">
          {item.primaryText && (
            <div className="flex justify-between items-center">
              <Typography className="uppercase font-semibold">
                {item.primaryText}
              </Typography>
            </div>
          )}
          {item.items.map(subItem => (
            <div
              className="flex justify-between items-center"
              key={subItem.dataId}
            >
              {subItem.primaryText && (
                <Typography className="uppercase font-semibold">
                  {subItem.primaryText}
                </Typography>
              )}
              {subItem.secondaryText && (
                <Typography className="pl-4">
                  {subItem.secondaryText}
                </Typography>
              )}
              <PriceFormatter
                amount={Number(subItem.price)}
                data-id={subItem.dataId}
                showOnlyCash
              />
            </div>
          ))}
        </li>
      );

    case "promocode":
      return (
        <li className="my-1">
          <div className="flex justify-between items-center">
            <Typography className="uppercase font-semibold">
              {item.primaryText}
              {item.isRemovable && (
                <button
                  className="inline-grid place-items-center ml-3 size-5 cursor-pointer transition duration-150 hover:text-red hover:scale-110 active:scale-95"
                  onClick={item.onRemove}
                  title={item.removeTitle}
                >
                  <CloseIcon size="sm" />
                </button>
              )}
            </Typography>
            <PriceFormatter
              amount={item.price}
              data-id={item.dataId}
              showOnlyCash
            />
          </div>
        </li>
      );

    case "typography":
      return (
        <Typography className={twMerge("my-2 text-sm", item.className)}>
          {item.content}
        </Typography>
      );

    default:
      return null;
  }
};

BasicItem.propTypes = {
  item: itemShape,
  theme: object,
};

const _CashFinancingContent = ({ structure }) => {
  return (
    <ul className="text-black list-none m-0">
      {structure?.map?.((item, idx) => {
        if (!item.isVisible) {
          return null;
        }

        const key = `${idx}-${item.type}`;

        switch (item.type) {
          case "group":
            return (
              <Fragment key={key}>
                {item.items?.map?.((subItem, subIdx) => (
                  <BasicItem
                    key={`${key}-s-${subIdx}`}
                    {...{ item: subItem }}
                  />
                ))}
              </Fragment>
            );

          default:
            return <BasicItem key={key} {...{ item }} />;
        }
      })}
    </ul>
  );
};

_CashFinancingContent.propTypes = {
  structure: arrayOf(itemShape),
};

function safeEqual(a, b) {
  try {
    return equal(a, b);
  } catch (error) {
    console.warn("Equality check failed", error);
    return false;
  }
}

export default memo(_CashFinancingContent, safeEqual);
